/**
 * Local Project Store
 *
 * Dumb persistence helper for localStorage operations for Writing Studio projects.
 * No business logic - just get/set/clear operations.
 * Business logic (like one-project limit) is enforced in the repository.
 */

import type { LocalWritingStudioState, LocalProject } from '@/types/writingStudio'
import { LOCAL_WRITING_STUDIO_VERSION, WRITING_STUDIO_STORAGE_KEYS } from '@/types/writingStudio'

/**
 * Storage health tracking
 */
export interface StorageHealthState {
  hasError: boolean
  lastError: string | null
  lastErrorAt: number | null
}

let storageHealth: StorageHealthState = {
  hasError: false,
  lastError: null,
  lastErrorAt: null,
}

/**
 * Gets current storage health state
 */
export function getStorageHealth(): StorageHealthState {
  return { ...storageHealth }
}

/**
 * Clears the storage error state
 */
export function clearStorageError(): void {
  storageHealth = {
    hasError: false,
    lastError: null,
    lastErrorAt: null,
  }
}

/**
 * Records a storage error
 */
function recordStorageError(error: string): void {
  storageHealth = {
    hasError: true,
    lastError: error,
    lastErrorAt: Date.now(),
  }
}

/**
 * Reads the local Writing Studio state for a child
 */
export function getLocalWritingStudioState(childId: string): LocalWritingStudioState | null {
  try {
    const key = WRITING_STUDIO_STORAGE_KEYS.getStudioKey(childId)
    const stored = localStorage.getItem(key)
    if (!stored) return null

    const parsed = JSON.parse(stored) as LocalWritingStudioState

    // Version check for future migrations
    if (parsed.version !== LOCAL_WRITING_STUDIO_VERSION) {
      console.warn('Unknown Writing Studio version:', parsed.version)
      return null
    }

    return parsed
  } catch (err) {
    console.error('Error reading Writing Studio state:', err)
    return null
  }
}

/**
 * Saves the local Writing Studio state for a child
 */
export function saveLocalWritingStudioState(state: LocalWritingStudioState): boolean {
  try {
    const key = WRITING_STUDIO_STORAGE_KEYS.getStudioKey(state.childId)
    localStorage.setItem(key, JSON.stringify(state))
    // Clear any previous errors on successful save
    if (storageHealth.hasError) {
      clearStorageError()
    }
    return true
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : 'Unknown storage error'
    console.error('Error saving Writing Studio state:', err)
    recordStorageError(errorMessage)
    return false
  }
}

/**
 * Gets or initializes the local Writing Studio state for a child
 */
export function getOrCreateLocalWritingStudioState(childId: string): LocalWritingStudioState {
  const existing = getLocalWritingStudioState(childId)
  if (existing) return existing

  const initial: LocalWritingStudioState = {
    version: 1,
    childId,
    projects: [],
  }

  saveLocalWritingStudioState(initial)
  return initial
}

/**
 * Gets all projects for a child
 */
export function getLocalProjects(childId: string): LocalProject[] {
  const state = getLocalWritingStudioState(childId)
  return state?.projects ?? []
}

/**
 * Gets a single project by ID
 */
export function getLocalProject(childId: string, projectId: string): LocalProject | null {
  const projects = getLocalProjects(childId)
  return projects.find((p) => p.id === projectId) ?? null
}

/**
 * Saves a project (creates or updates)
 */
export function saveLocalProject(childId: string, project: LocalProject): boolean {
  const state = getOrCreateLocalWritingStudioState(childId)
  const existingIndex = state.projects.findIndex((p) => p.id === project.id)

  if (existingIndex >= 0) {
    state.projects[existingIndex] = project
  } else {
    state.projects.push(project)
  }

  return saveLocalWritingStudioState(state)
}

/**
 * Deletes a project
 */
export function deleteLocalProject(childId: string, projectId: string): boolean {
  const state = getOrCreateLocalWritingStudioState(childId)
  state.projects = state.projects.filter((p) => p.id !== projectId)
  return saveLocalWritingStudioState(state)
}

/**
 * Gets the number of projects for a child
 */
export function getLocalProjectCount(childId: string): number {
  return getLocalProjects(childId).length
}

/**
 * Clears the local Writing Studio state completely
 */
export function clearLocalWritingStudioState(childId: string): void {
  const key = WRITING_STUDIO_STORAGE_KEYS.getStudioKey(childId)
  localStorage.removeItem(key)
}
